import { useEffect, useRef } from "react";
import { EditorState, Prec } from "@codemirror/state";
import { EditorView, keymap, placeholder as cmPlaceholder } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, insertNewlineAndIndent } from "@codemirror/commands";
import {
  autocompletion,
  completionKeymap,
  type Completion,
  type CompletionContext,
  type CompletionResult,
} from "@codemirror/autocomplete";
import {
  CLASS_LEXICON,
  PROPERTY_LEXICON,
  SUPERLATIVE_LEXICON,
  type EntityLinker,
} from "@nl2sparql/core";

/**
 * The question box. Built on CodeMirror 6 (@codemirror/autocomplete) so typing
 * is assisted two ways:
 *   1. a static lexicon of relations / classes / superlatives / question stems, and
 *   2. live, typo-tolerant Wikidata entity suggestions (the fuzzy `wbsearchentities`
 *      API doubles as autocorrect — "chirstopher nolan" still finds Q25191).
 * Enter submits; Shift-Enter inserts a newline.
 */

interface NlQueryBoxProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  entityLinker: EntityLinker;
  disabled?: boolean;
}

// Static completions from the curated lexicons (built once).
const STATIC_OPTIONS: Completion[] = buildStaticOptions();

function buildStaticOptions(): Completion[] {
  const seen = new Set<string>();
  const out: Completion[] = [];
  const add = (label: string, type: string, detail: string) => {
    if (seen.has(label)) return;
    seen.add(label);
    out.push({ label, type, detail });
  };
  for (const stem of [
    "capital of",
    "population of",
    "author of",
    "how many",
    "who directed",
    "films directed by",
    "largest",
    "most populous",
  ]) {
    add(stem, "keyword", "question stem");
  }
  for (const phrase of Object.keys(PROPERTY_LEXICON)) add(phrase, "property", "relation");
  for (const noun of Object.keys(CLASS_LEXICON)) add(noun, "class", "class");
  for (const adj of Object.keys(SUPERLATIVE_LEXICON)) add(adj, "keyword", "superlative");
  return out;
}

// Small module-level cache so repeated keystrokes don't re-hit the API.
const entityCache = new Map<string, Completion[]>();

function makeCompletionSource(entityLinker: EntityLinker) {
  return async (context: CompletionContext): Promise<CompletionResult | null> => {
    const word = context.matchBefore(/[\w'’-]+/);
    if (!word || (word.from === word.to && !context.explicit)) return null;
    const typed = context.state.sliceDoc(word.from, word.to).toLowerCase();

    const local = STATIC_OPTIONS.filter((o) => o.label.toLowerCase().startsWith(typed));

    let entities: Completion[] = [];
    if (typed.length >= 4 || context.explicit) {
      const cached = entityCache.get(typed);
      if (cached) {
        entities = cached;
      } else {
        try {
          const candidates = await entityLinker.link(typed, { limit: 6 });
          entities = candidates.map((c) => ({
            label: c.label,
            type: "variable",
            detail: c.description ? c.description.slice(0, 48) : (c.id ?? "entity"),
            boost: -1,
          }));
          entityCache.set(typed, entities);
        } catch {
          entities = [];
        }
      }
    }

    const options = [...local, ...entities];
    if (options.length === 0) return null;
    return { from: word.from, options, validFor: /^[\w'’-]*$/ };
  };
}

export function NlQueryBox({ value, onChange, onSubmit, entityLinker, disabled }: NlQueryBoxProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const onSubmitRef = useRef(onSubmit);
  onChangeRef.current = onChange;
  onSubmitRef.current = onSubmit;

  // Create the editor once.
  useEffect(() => {
    if (!hostRef.current) return;
    const submitKeymap = Prec.highest(
      keymap.of([
        {
          key: "Enter",
          run: () => {
            onSubmitRef.current();
            return true;
          },
        },
        { key: "Shift-Enter", run: insertNewlineAndIndent },
      ]),
    );

    const view = new EditorView({
      parent: hostRef.current,
      state: EditorState.create({
        doc: value,
        extensions: [
          history(),
          EditorView.lineWrapping,
          cmPlaceholder("Ask Wikidata anything — e.g. “films directed by Christopher Nolan”"),
          autocompletion({
            override: [makeCompletionSource(entityLinker)],
            activateOnTyping: true,
            icons: false,
          }),
          submitKeymap,
          keymap.of([...defaultKeymap, ...historyKeymap, ...completionKeymap]),
          EditorView.updateListener.of((u) => {
            if (u.docChanged) onChangeRef.current(u.state.doc.toString());
          }),
          EditorView.editable.of(!disabled),
        ],
      }),
    });
    viewRef.current = view;
    view.focus();
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityLinker]);

  // Keep the editor in sync when the parent changes `value` (example chips).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (value !== current) {
      view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
    }
  }, [value]);

  return (
    <div
      ref={hostRef}
      className="rounded-xl border"
      style={{
        borderColor: "var(--nl2s-border-strong)",
        boxShadow: "var(--nl2s-shadow)",
        opacity: disabled ? 0.7 : 1,
      }}
    />
  );
}
